// Copyright (c) 2024-2026 AlphaGrade. All Rights Reserved.
//
// Public Terms of Service. Rendered by LoginGate for /terms regardless of auth
// so it's reachable by logged-out visitors and carrier vetting.

import React from 'react';

const wrap = {
  maxWidth: 760,
  margin: '0 auto',
  padding: '2.5rem 1.25rem 4rem',
  color: '#cbd5e1',
  fontFamily: '-apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
  lineHeight: 1.65,
  fontSize: '0.98rem',
};
const h1 = { color: '#fff', fontSize: '1.9rem', margin: '0 0 0.25rem' };
const h2 = { color: '#fff', fontSize: '1.15rem', margin: '2rem 0 0.5rem' };
const muted = { color: '#94a3b8', fontSize: '0.85rem', margin: '0 0 1.5rem' };
const link = { color: '#00D4AA', textDecoration: 'none' };

export default function TermsOfService() {
  return (
    <div style={{ background: '#0E1525', minHeight: '100vh' }}>
      <div style={wrap}>
        <a href="/" style={{ ...link, fontSize: '0.85rem' }}>&larr; Back to AlphaGrade</a>
        <h1 style={h1}>Terms of Service</h1>
        <p style={muted}>Last updated: June 14, 2026</p>

        <p>
          These Terms of Service ("Terms") govern your use of AlphaGrade, a service
          operated by Hendrix, LLC ("AlphaGrade," "we," "us"), at alphagrade.io (the
          "Service"). By creating an account or using the Service, you agree to
          these Terms.
        </p>

        <h2 style={h2}>What AlphaGrade Is</h2>
        <p>
          AlphaGrade is a research and analytics tool. It organizes market data,
          produces transparent scores, and provides an AI research assistant to
          help you research investments more efficiently.
        </p>

        <h2 style={h2}>Not Financial Advice</h2>
        <p>
          <strong>
            AlphaGrade is not a financial advisor, broker-dealer, or registered
            investment adviser, and nothing in the Service is financial, investment,
            legal, or tax advice.
          </strong>{' '}
          Scores, grades, and summaries are quantitative analysis based on publicly
          available data and are provided for informational and educational purposes
          only. They are not recommendations to buy or sell any security. You are
          solely responsible for your own investment decisions. Always do your own
          research and consider consulting a licensed professional. Past performance
          does not guarantee future results, and all investing involves risk,
          including possible loss of principal.
        </p>

        <h2 style={h2}>Eligibility &amp; Accounts</h2>
        <p>
          You must be at least 18 years old to use the Service. You are responsible
          for keeping your account credentials secure and for activity under your
          account.
        </p>

        <h2 style={h2}>Subscriptions &amp; Billing</h2>
        <p>
          The Service offers a free tier and paid subscription plans. Paid plans are
          billed through Stripe on a recurring basis until cancelled. You can cancel
          at any time through Stripe's customer portal; cancellation takes effect at
          the end of the current billing period.
        </p>

        <h2 style={h2}>SMS Alerts</h2>
        <p>
          SMS alerts are optional and require opt-in. Message frequency varies and
          message and data rates may apply. Reply STOP to unsubscribe or HELP for
          help. See our{' '}
          <a href="/privacy" style={link}>Privacy Policy</a> for how we handle your
          mobile information.
        </p>

        <h2 style={h2}>Acceptable Use</h2>
        <p>
          You agree not to misuse the Service, attempt to disrupt or reverse-engineer
          it, scrape it at scale, or use it for any unlawful purpose.
        </p>

        <h2 style={h2}>No Warranties &amp; Limitation of Liability</h2>
        <p>
          The Service is provided "as is" without warranties of any kind. Market data
          may be delayed or contain errors. To the fullest extent permitted by law,
          AlphaGrade is not liable for any losses or damages arising from your use of
          the Service or any investment decision you make.
        </p>

        <h2 style={h2}>Changes</h2>
        <p>We may update these Terms; continued use after changes constitutes acceptance.</p>

        <h2 style={h2}>Contact</h2>
        <p>
          Questions? Email <a href="mailto:support@alphagrade.io" style={link}>support@alphagrade.io</a>.
        </p>
      </div>
    </div>
  );
}
